import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'hooks';
import { Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import { Button, ErrorResponse } from '~components';
import Icon, { faFileLines, faPlus } from '~components/icon';
import { Row, TableView } from '~components/table-view';

import { AuthTags } from '~constants/authTags';
import { selectUserTags } from '~main/selectors/login';

import { useToast } from '~libs/toast';
import { getErrorMessage } from '~libs/axiosWrapper';

import { useCreateTemplateMutation, useGetTemplatesQuery } from '~forms/actions/templates';
import { getTemplateColumns } from '~forms/forms/list';
import ClientPickerModal from './ClientPickerModal';

const FormTemplateList: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { data: templates, isLoading, isError, error } = useGetTemplatesQuery();
  const [createTemplate, { isLoading: isCreating }] = useCreateTemplateMutation();

  // Creating or filling a form both require forms:Edit.
  const tags = useAppSelector(selectUserTags);
  const canEdit = tags.includes(AuthTags.FORMS.EDIT);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');

  // Fill: track which template the user clicked Fill on
  const [fillTemplate, setFillTemplate] = useState<Row | null>(null);

  const openTemplate = useCallback((row: Row) => navigate(`/forms/templates/${row.id}`), [navigate]);
  const openFill = useCallback((row: Row) => setFillTemplate(row), []);

  const columns = useMemo(
    () => getTemplateColumns(openTemplate, openFill, canEdit),
    [openTemplate, openFill, canEdit],
  );

  const handleOpenModal = () => {
    setNewTitle('');
    setNewDescription('');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => setIsModalOpen(false);

  const handleCreate = async () => {
    if (isCreating) return; // guard against duplicate creates while the mutation is in-flight
    const trimmedTitle = newTitle.trim();
    if (!trimmedTitle) {
      showToast('danger', 'Title is required');
      return;
    }
    try {
      const result = await createTemplate({ title: trimmedTitle, description: newDescription.trim() || undefined }).unwrap();
      showToast('success', `Form "${result.template.title}" created`);
      setIsModalOpen(false);
      navigate(`/forms/templates/${result.template.id}`);
    } catch (err) {
      showToast('danger', `Failed to create form: ${getErrorMessage(err)}`);
    }
  };

  return (
    <div>
      {isError && <ErrorResponse message={getErrorMessage(error)} dataCy="forms-error-response" />}

      <div className="nav d-flex justify-content-between bg-light p-2 mt-2 mb-2">
        <div>
          <Button
            color="success"
            size="sm"
            className="ms-2"
            onClick={handleOpenModal}
            disabled={!canEdit}
            title={canEdit ? undefined : 'You do not have permission to create forms'}
            dataCy="forms-new-btn"
          >
            <Icon className="me-2" icon={faPlus} />
            New Form
          </Button>
        </div>
      </div>

      <TableView
        id="forms-templates"
        data={(templates ?? []) as Row[]}
        columns={columns}
        readonly
        striped
        isLoading={isLoading}
        emptyIcon={faFileLines}
        emptyMessage="No forms yet. Create your first form to get started."
      />

      {/* New form modal */}
      <Modal isOpen={isModalOpen} toggle={handleCloseModal} data-cy="forms-new-modal">
        <ModalHeader toggle={handleCloseModal}>New Form</ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label for="new-form-title">Title *</Label>
            <Input
              id="new-form-title"
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Form title"
              data-cy="new-form-title-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isCreating) {
                  e.preventDefault();
                  void handleCreate();
                }
              }}
            />
          </div>
          <div className="mb-3">
            <Label for="new-form-description">Description</Label>
            <Input
              id="new-form-description"
              type="textarea"
              rows={3}
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Optional description"
              data-cy="new-form-description-input"
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="info" outline onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button color="success" onClick={handleCreate} loading={isCreating} dataCy="new-form-submit-btn">
            Create Form
          </Button>
        </ModalFooter>
      </Modal>

      {/* Client picker modal — opens when user clicks Fill on a template */}
      <ClientPickerModal
        templateId={fillTemplate?.id as string | undefined}
        isOpen={!!fillTemplate}
        onCancel={() => setFillTemplate(null)}
      />
    </div>
  );
};

export default FormTemplateList;
